/* Licence — what this salon has bought, and what that switches on.
 *
 * The one page in this app that changes a customer's console without changing
 * any of their settings. Everything else here edits something the salon could
 * have edited itself, given the screen; this decides which screens they get.
 *
 * Three things on it, in the order somebody actually needs them:
 *
 *   what is in force   the reading, with what it entitles and what it costs
 *                      them if it goes — see `bite` in js/rules/licensing.js.
 *   granting           a new grant, never an edit of the one that is there.
 *   the history        every grant this salon has ever held, labelled.
 *
 * The history is the reason the model is what it is. A licence held as one
 * mutable row would answer "what are they on" and nothing else; the question
 * that actually gets asked six months later is "when did they go up to
 * Standard, and who moved them", and that is a question about rows nobody
 * overwrote. See admin/server/schema/14-licensing.sql.
 *
 * ── WHAT THIS IS NOT ────────────────────────────────────────────────────────
 *
 * Not billing. Nothing here takes money, knows a price or talks to anything
 * that does — a grant is the record of a decision that was made elsewhere. And
 * not suspension: a lapsed licence takes the screens away and leaves every row
 * in place, a suspension (Danger zone) leaves the screens and takes the data
 * away. They are different levers and neither is the other turned up.
 */
import * as Vue from 'vue';
import { api } from '../api.js';
import { nav } from '../nav.js';
import { store } from '../store.js';
import { licensing } from '../rules/licensing.js';
import { ConfirmButton } from '../components/confirm-button.js';

var Licensing = {
  name: 'AdminLicensing',
  components: { ConfirmButton: ConfirmButton },
  setup: function () {
    var planId = Vue.ref('');
    var termDays = Vue.ref(365);
    var startNow = Vue.ref(true);
    var extras = Vue.ref({});
    var seatOverride = Vue.ref('');
    var note = Vue.ref('');
    var revokeReason = Vue.ref('');

    Vue.watch(store.selectedId, function (id) {
      revokeReason.value = '';
      note.value = '';
      if (id) store.loadLicense(id);
    }, { immediate: true });

    /* The form is seeded from what they already have, not from a blank.
     *
     * The overwhelmingly common grant is "the same again for another year", and
     * a form that opened on the first plan in the list would make that the one
     * grant somebody has to be careful about. Seeded once per salon rather than
     * bound, so it stops following the store the moment anybody touches it.
     */
    var seeded = Vue.ref(null);
    Vue.watchEffect(function () {
      var id = store.selectedId.value;
      if (!id || seeded.value === id) return;
      if (!store.plansLoaded.value || store.license.loadedFor !== id) return;
      var e = store.entitlement.value;
      var fallback = store.defaultPlan.value || store.offeredPlans.value[0];
      planId.value = e.planId || (fallback && fallback.id) || '';
      seatOverride.value = e.grant && e.grant.seats != null ? String(e.grant.seats) : '';
      var picked = {};
      e.extras.forEach(function (f) { picked[f] = true; });
      extras.value = picked;
      seeded.value = id;
    });

    var plan = Vue.computed(function () {
      return store.plans.value.find(function (p) { return p.id === planId.value; }) || null;
    });

    /* The feature list the form shows: everything this build knows about, plus
     * anything the plan or the current grant carries that it does not.
     *
     * The second half matters. A feature id exists because a build looks for
     * it, so an admin app one release behind its console would otherwise
     * quietly drop an extra from the form and then grant a licence without it.
     * Unknown ids are shown, marked, and kept.
     */
    var featureRows = Vue.computed(function () {
      var inPlan = {};
      ((plan.value && plan.value.features) || []).forEach(function (f) { inPlan[f] = true; });

      var seen = {};
      var out = licensing.FEATURES.map(function (f) {
        seen[f.id] = true;
        return {
          id: f.id, label: f.label, hint: f.hint, bite: f.bite,
          known: true, inPlan: !!inPlan[f.id]
        };
      });

      var strays = Object.keys(inPlan).concat(Object.keys(extras.value));
      strays.forEach(function (id) {
        if (seen[id] || !id) return;
        seen[id] = true;
        out.push({
          id: id, label: id,
          hint: 'This build of the admin app does not know this feature. It is kept and granted as it stands.',
          bite: '', known: false, inPlan: !!inPlan[id]
        });
      });
      return out;
    });

    function toggleExtra(row) {
      if (row.inPlan) return;
      var next = Object.assign({}, extras.value);
      if (next[row.id]) delete next[row.id]; else next[row.id] = true;
      extras.value = next;
    }

    var pickedExtras = Vue.computed(function () {
      return Object.keys(extras.value).filter(function (k) { return extras.value[k]; });
    });

    // What the grant about to be made would entitle — the union, before it is
    // made. A screen that only showed the answer afterwards would make the
    // difference between a plan and its extras something you find out by
    // saving.
    var preview = Vue.computed(function () {
      return licensing.featuresOf(plan.value, { features: pickedExtras.value });
    });

    /* When it starts, and it is not always now.
     *
     * A renewal runs on from the end of the current term. Renewing a licence
     * with three weeks left and having those three weeks evaporate is a mistake
     * that is invisible on this screen and obvious on the invoice, so it is the
     * default — `startNow` is the deliberate override for the case where
     * somebody is correcting a grant rather than extending one.
     */
    var startsAt = Vue.computed(function () {
      if (startNow.value) return new Date().toISOString();
      return licensing.renewalStart(store.entitlement.value.grant, Date.now());
    });

    var expiresAt = Vue.computed(function () {
      return licensing.termEnd(startsAt.value, termDays.value);
    });

    var canRunOn = Vue.computed(function () {
      var e = store.entitlement.value;
      return !!(e.grant && e.expiresAt && !e.left.expired);
    });

    async function grant() {
      if (!planId.value) { api.toast('Pick a plan.', 'bad'); return; }
      var okay = await store.grantLicense({
        planId: planId.value,
        features: pickedExtras.value,
        seats: seatOverride.value,
        startsAt: startsAt.value,
        expiresAt: expiresAt.value,
        note: note.value
      });
      if (!okay) return;
      note.value = '';
      api.toast(startNow.value
        ? 'Granted. Their console follows within a page load.'
        : 'Granted, starting when the current term ends. Nothing changes for them today.');
    }

    async function revoke(row) {
      var current = store.entitlement.value.grant;
      var wasInForce = !!(current && current.id === row.id);
      if (!await store.revokeGrant(row, revokeReason.value)) return;
      revokeReason.value = '';
      var after = store.entitlement.value;
      api.toast(wasInForce
        ? (after.grant
            ? 'Withdrawn. They fall back to their ' + (after.planName || 'earlier') + ' grant.'
            : 'Withdrawn. This salon now has no licence at all.')
        : 'Withdrawn. It was not the one in force, so nothing changed for them.');
    }

    /* Every grant, labelled against the one in force. `superseded` and
     * `revoked` are deliberately different words: one was renewed, the other
     * was taken away, and only the second needs explaining to anybody.
     *
     * `Date.now()` inside a computed would normally be a stale reading, and is
     * not one here: this depends on `entitlement`, which depends on the store's
     * ticking clock, so the whole list is recomputed every fifteen seconds and
     * a term that ends while somebody is looking at it changes the row.
     */
    var history = Vue.computed(function () {
      var current = store.entitlement.value.grant;
      return store.license.rows.map(function (r) {
        var state = licensing.rowState(r, current, Date.now());
        return {
          row: r,
          state: state,
          kind: licensing.stateKind(state),
          planName: (store.plans.value.find(function (p) { return p.id === r.plan_id; }) || {}).name
            || r.plan_id,
          extras: licensing.normalise(r.features)
        };
      });
    });

    var lapsedBite = Vue.computed(function () {
      return licensing.FEATURES.filter(function (f) {
        return store.entitlement.value.features.indexOf(f.id) !== -1;
      });
    });

    return {
      t: store.selected,
      e: store.entitlement,
      seats: store.seats,
      plans: store.offeredPlans,
      loading: store.license,
      planId: planId,
      plan: plan,
      terms: licensing.TERMS,
      termDays: termDays,
      startNow: startNow,
      startsAt: startsAt,
      expiresAt: expiresAt,
      canRunOn: canRunOn,
      featureRows: featureRows,
      extras: extras,
      toggleExtra: toggleExtra,
      preview: preview,
      seatOverride: seatOverride,
      note: note,
      grant: grant,
      revoke: revoke,
      revokeReason: revokeReason,
      history: history,
      lapsedBite: lapsedBite,
      stamp: store.stamp,
      ago: store.ago,
      goPage: nav.goPage
    };
  },
  template: [
    '<div class="page">',
    '  <div class="page__head">',
    '    <h2>Licence</h2>',
    '    <p class="page__lead">Which of the product this salon has bought. It decides what',
    '      their console <strong>offers</strong>; it does not decide what they may',
    '      <strong>reach</strong> &mdash; that is the policies, and a lapsed licence takes',
    '      nothing away from the database.</p>',
    '  </div>',
    '',
    '  <p v-if="!t" class="empty">Loading…</p>',
    '  <template v-else>',
    '',
    /* ---------------- what is in force ---------------- */
    '  <div class="card" :class="e.state === \'active\' ? \'\' : \'card--warn\'">',
    '    <div class="card__head">',
    '      <h3>{{ e.planName || "No licence" }}</h3>',
    '      <span class="tag" :class="e.licensed ? \'tag--set\' : \'tag--bad\'">{{ e.state }}</span>',
    '    </div>',
    '',
    '    <p v-if="e.state === \'none\'" class="warn-line">This salon has never been granted a',
    '      licence, or every grant it had has been withdrawn. Its console offers nothing at',
    '      all &mdash; the board is a card saying it is not licensed.</p>',
    '    <p v-else-if="e.state === \'lapsed\'" class="warn-line">Their term ended',
    '      {{ ago(e.expiresAt) }}. The screens are gone; every row they ever wrote is still',
    '      there and comes straight back when this is granted again.</p>',
    '    <p v-else-if="e.expiring" class="warn-line">This runs out in',
    '      <strong>{{ e.left.label }}</strong>, on {{ stamp(e.expiresAt) }}. Renewing below',
    '      runs the new term on from that date rather than from today.</p>',
    '',
    '    <dl class="facts">',
    '      <div><dt>Plan</dt><dd>{{ e.planName || "—" }}</dd></div>',
    '      <div><dt>Started</dt><dd :title="stamp(e.startsAt)">{{ e.startsAt ? stamp(e.startsAt) : "—" }}</dd></div>',
    '      <div><dt>Ends</dt>',
    '        <dd>{{ e.expiresAt ? stamp(e.expiresAt) : "no end date" }}',
    '          <span v-if="e.expiresAt" class="grid__sub">{{ e.left.label }}</span></dd></div>',
    '      <div><dt>Seats</dt>',
    '        <dd>{{ seats.label }}',
    '          <button type="button" class="link-btn" @click="goPage(\'members\')">People</button></dd></div>',
    '    </dl>',
    '',
    '    <p v-if="seats.over" class="warn-line">More accounts are attached than the licence',
    '      allows. Nobody is signed out by that &mdash; the seat limit is checked when somebody',
    '      is <em>attached</em>, so this salon simply cannot add another.</p>',
    '',
    '    <template v-if="e.licensed">',
    '      <p class="hint">What this switches on in their console. Everything not listed here',
    '        behaves as though the product had never had it.</p>',
    '      <ul class="notes">',
    '        <li v-for="f in lapsedBite" :key="f.id"><strong>{{ f.label }}</strong> &mdash; {{ f.bite }}</li>',
    '      </ul>',
    '      <p v-if="!lapsedBite.length" class="hint">Their plan grants only features this build',
    '        of the admin app does not recognise: <span class="mono">{{ e.features.join(", ") }}</span>.</p>',
    '    </template>',
    '  </div>',
    '',
    /* ---------------- granting ---------------- */
    '  <div class="card">',
    '    <h3>Grant a licence</h3>',
    '    <p class="hint">Always a new grant, never an edit of the one above &mdash; renewing,',
    '      moving them up a plan and correcting a mistake all leave the previous grant legible.',
    '      The newest grant that has started is the one in force.</p>',
    '',
    '    <div class="card__row">',
    '      <label class="field">',
    '        <span class="field__label">Plan</span>',
    '        <select v-model="planId" class="input">',
    '          <option v-for="p in plans" :key="p.id" :value="p.id">{{ p.name }}</option>',
    '        </select>',
    '      </label>',
    '      <label class="field">',
    '        <span class="field__label">Term</span>',
    '        <div class="pick" role="radiogroup" aria-label="Term">',
    '          <button v-for="opt in terms" :key="String(opt.days)" type="button" class="pick__opt"',
    '            :class="{ \'is-on\': termDays === opt.days }" role="radio"',
    '            :aria-checked="termDays === opt.days ? \'true\' : \'false\'"',
    '            @click="termDays = opt.days">{{ opt.label }}</button>',
    '        </div>',
    '      </label>',
    '      <label class="field">',
    '        <span class="field__label">Seats</span>',
    '        <input v-model="seatOverride" class="input input--mins" type="number" min="1" max="999"',
    '          :placeholder="plan && plan.seats != null ? String(plan.seats) : \'no limit\'" />',
    '      </label>',
    '    </div>',
    '    <p class="hint">An empty seat box takes the plan&rsquo;s own limit',
    '      &mdash; <strong>{{ plan && plan.seats != null ? plan.seats : "no limit" }}</strong>',
    '      for {{ plan ? plan.name : "this plan" }}. A number here overrides it for this salon only.</p>',
    '',
    '    <div v-if="canRunOn" class="card__row">',
    '      <div class="pick" role="radiogroup" aria-label="When it starts">',
    '        <button type="button" class="pick__opt" :class="{ \'is-on\': !startNow }" role="radio"',
    '          :aria-checked="!startNow ? \'true\' : \'false\'" @click="startNow = false">',
    '          Run on from {{ stamp(e.expiresAt) }}</button>',
    '        <button type="button" class="pick__opt" :class="{ \'is-on\': startNow }" role="radio"',
    '          :aria-checked="startNow ? \'true\' : \'false\'" @click="startNow = true">',
    '          Start now</button>',
    '      </div>',
    '      <p class="hint hint--inline">Running on keeps the days they have left. Starting now',
    '        throws them away, which is what you want when you are replacing a grant rather',
    '        than extending one.</p>',
    '    </div>',
    '',
    '    <h4 class="page__sub">Features</h4>',
    '    <p class="hint">Ticked and locked means the plan includes it. Anything else you tick is',
    '      granted to this salon on top of the plan &mdash; a grant can add to a plan and never',
    '      take away from one, or the plan would mean something different for every salon on it.</p>',
    '    <ul class="rows rows--tight">',
    '      <li v-for="f in featureRows" :key="f.id" class="row row--slim">',
    '        <label class="switch switch--sm">',
    '          <input type="checkbox" :checked="f.inPlan || !!extras[f.id]" :disabled="f.inPlan"',
    '            @change="toggleExtra(f)" />',
    '          <span class="switch__track"><span class="switch__thumb"></span></span>',
    '        </label>',
    '        <span class="row__main">',
    '          <span class="row__name">{{ f.label }}',
    '            <span v-if="f.inPlan" class="tag tag--set">in the plan</span>',
    '            <span v-else-if="!f.known" class="tag tag--bad">unknown to this build</span></span>',
    '          <span class="row__desc">{{ f.hint }}</span>',
    '        </span>',
    '        <span class="row__key mono">{{ f.id }}</span>',
    '      </li>',
    '    </ul>',
    '',
    '    <div class="card__row">',
    '      <label class="field field--grow">',
    '        <span class="field__label">Why (recorded on the grant and in the log)</span>',
    '        <input v-model="note" class="input" type="text" maxlength="200"',
    '          placeholder="Signed up, upgraded, extended while they decide" />',
    '      </label>',
    '      <button type="button" class="btn btn--primary" @click="grant">Grant</button>',
    '    </div>',
    '',
    '    <p class="hint">This would grant',
    '      <strong>{{ preview.length }}</strong> feature{{ preview.length === 1 ? "" : "s" }}',
    '      &mdash; <span class="mono">{{ preview.join(", ") || "none" }}</span> &mdash;',
    '      from {{ stamp(startsAt) }}',
    '      {{ expiresAt ? "until " + stamp(expiresAt) : "with no end date" }}.</p>',
    '  </div>',
    '',
    /* ---------------- the history ---------------- */
    '  <h3 class="page__sub">Every grant</h3>',
    '  <p class="hint">Nothing in this list was ever edited and nothing can be deleted &mdash;',
    '    the table has no delete grant at all. <strong>Superseded</strong> means a later grant',
    '    replaced it; <strong>revoked</strong> means somebody withdrew it, which is a different',
    '    fact and the one that needs explaining.</p>',
    '',
    '  <p v-if="loading.loading" class="empty">Loading grants…</p>',
    '  <p v-else-if="!history.length" class="empty">This salon has never been granted a licence.</p>',
    '',
    '  <table v-else class="grid grid--plain">',
    '    <thead><tr><th>Plan</th><th>Term</th><th>Extras</th><th>Seats</th><th>Note</th>',
    '      <th>State</th><th></th></tr></thead>',
    '    <tbody>',
    '      <tr v-for="h in history" :key="h.row.id"',
    '        :class="{ \'is-muted\': h.state === \'superseded\' || h.state === \'revoked\' }">',
    '        <td><span class="grid__name">{{ h.planName }}</span>',
    '          <span class="grid__sub" :title="stamp(h.row.created_at)">granted {{ ago(h.row.created_at) }}</span></td>',
    '        <td>{{ stamp(h.row.starts_at) }}',
    '          <span class="grid__sub">{{ h.row.expires_at ? "to " + stamp(h.row.expires_at) : "no end date" }}</span></td>',
    '        <td class="mono">{{ h.extras.join(", ") || "—" }}</td>',
    '        <td>{{ h.row.seats == null ? "plan" : h.row.seats }}</td>',
    '        <td>{{ h.row.note || "—" }}</td>',
    '        <td><span class="pill" :class="h.kind ? \'pill--\' + h.kind : \'\'">{{ h.state }}</span></td>',
    '        <td>',
    '          <confirm-button v-if="!h.row.revoked_at" label="Revoke" size="sm"',
    '            :confirm="h.state === \'in force\' ? \'Withdraw — their console loses it now\' : \'Withdraw this grant\'"',
    '            @confirmed="revoke(h.row)" />',
    '          <span v-else class="grid__sub" :title="stamp(h.row.revoked_at)">{{ ago(h.row.revoked_at) }}</span>',
    '        </td>',
    '      </tr>',
    '    </tbody>',
    '  </table>',
    '',
    '  <div class="card">',
    '    <h3>Withdrawing one</h3>',
    '    <p class="hint">Revoking the grant in force does <strong>not</strong> put the salon back',
    '      on the one before it &mdash; a superseded grant stays superseded. It takes them to',
    '      whichever earlier grant is still standing, and to nothing if none is. Use it for a',
    '      grant that should not have been made; use a new grant for everything else.</p>',
    '    <label class="field field--grow">',
    '      <span class="field__label">Reason, kept on the grant</span>',
    '      <input v-model="revokeReason" class="input" type="text" maxlength="200"',
    '        placeholder="Typed before pressing Revoke above" />',
    '    </label>',
    '  </div>',
    '',
    '  </template>',
    '</div>'
  ].join('\n')
};

nav.register({
  id: 'licensing',
  name: 'Licence',
  group: 'Salon',
  page: Licensing
});

export { Licensing };
